import { Fragment, useEffect, useMemo, useState } from "react";
import { Popover, Transition } from "@headlessui/react";
import { CalendarDaysIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  format,
  getYear,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths,
} from "date-fns";
import { SelectDropdown } from "./SelectDropdown";

const toIsoDate = (date) => format(date, "yyyy-MM-dd");

const safeParseIso = (value) => {
  if (!value || typeof value !== "string") return null;
  try {
    const d = parseISO(value);
    // parseISO can return Invalid Date; guard by checking time value
    // eslint-disable-next-line no-restricted-globals
    if (isNaN(d.getTime())) return null;
    return d;
  } catch {
    return null;
  }
};

const buildCalendarDays = (monthDate) => {
  const start = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 0 });
  const end = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 0 });

  const days = [];
  let cursor = start;
  while (cursor <= end) {
    days.push(cursor);
    cursor = addDays(cursor, 1);
  }
  return days;
};

export const DatePickerInput = ({
  value,
  onChange,
  onPaste,
  placeholder = "Pick a date",
  disabled = false,
  pickerClassName = "",
  min,
  max,
  className = "",
  buttonClassName = "",
  /** date-fns format for the selected date label (e.g. `MM/dd/yyyy` → 11/22/2023). */
  dateFormat = "MMM d, yyyy",
  /** `end` = label then calendar icon (sidebar rows). */
  iconPlacement = "start",
  /** `sidebar` = borderless trigger, row `group-hover` underline, anchor panel to end. */
  buttonPreset = "default",
  /** Optional list of exact ISO dates that may be selected. */
  allowedDates = null,
  /** Optional quick-range rail rendered left of the calendar: `{ label, onSelect, isActive }`. */
  presets = null,
}) => {
  const today = useMemo(() => new Date(), []);
  const selectedDate = useMemo(() => safeParseIso(value), [value]);
  const minDate = useMemo(() => safeParseIso(min), [min]);
  const maxDate = useMemo(() => safeParseIso(max), [max]);
  const allowedDatesSet = useMemo(() => {
    if (!Array.isArray(allowedDates) || !allowedDates.length) return null;
    return new Set(allowedDates.filter(Boolean));
  }, [allowedDates]);

  const [visibleMonth, setVisibleMonth] = useState(() => startOfMonth(selectedDate || today));

  useEffect(() => {
    if (selectedDate) {
      setVisibleMonth(startOfMonth(selectedDate));
    }
  }, [selectedDate]);

  const days = useMemo(() => buildCalendarDays(visibleMonth), [visibleMonth]);

  const clampVisibleMonth = (candidate) => {
    let m = startOfMonth(candidate);
    if (minDate && isBefore(m, startOfMonth(minDate))) return startOfMonth(minDate);
    if (maxDate && isAfter(m, startOfMonth(maxDate))) return startOfMonth(maxDate);
    return m;
  };

  /** When month/year navigation changes the visible month, map it to a concrete date and notify parent. */
  const commitSelectionForVisibleMonth = (monthStart) => {
    if (disabled || !onChange) return;
    const monthStartNorm = startOfMonth(monthStart);
    const lastDay = endOfMonth(monthStartNorm);
    const day =
      selectedDate != null
        ? Math.min(selectedDate.getDate(), lastDay.getDate())
        : 1;
    let next = new Date(monthStartNorm.getFullYear(), monthStartNorm.getMonth(), day);
    if (minDate && isBefore(next, minDate)) next = minDate;
    if (maxDate && isAfter(next, maxDate)) next = maxDate;
    if (allowedDatesSet) {
      if (allowedDatesSet.has(toIsoDate(next))) {
        onChange(toIsoDate(next));
        return;
      }
      let cursor = monthStartNorm;
      while (cursor <= lastDay) {
        if (!isDisabledDay(cursor)) {
          onChange(toIsoDate(cursor));
          return;
        }
        cursor = addDays(cursor, 1);
      }
      return;
    }
    onChange(toIsoDate(next));
  };

  const monthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: format(new Date(2000, i, 1), "MMM"),
      })),
    [],
  );

  const yearOptions = useMemo(() => {
    const yNow = visibleMonth.getFullYear();
    let low = minDate ? getYear(startOfMonth(minDate)) : yNow - 50;
    let high = maxDate ? getYear(startOfMonth(maxDate)) : yNow + 50;
    if (low > high) {
      const t = low;
      low = high;
      high = t;
    }
    const opts = [];
    for (let y = low; y <= high; y++) {
      opts.push({ value: y, label: String(y) });
    }
    return opts;
  }, [minDate, maxDate, visibleMonth]);

  const prevMonthDisabled =
    disabled ||
    clampVisibleMonth(subMonths(visibleMonth, 1)).getTime() === visibleMonth.getTime();
  const nextMonthDisabled =
    disabled ||
    clampVisibleMonth(addMonths(visibleMonth, 1)).getTime() === visibleMonth.getTime();

  const isDisabledDay = (day) => {
    if (minDate && isBefore(day, minDate)) return true;
    if (maxDate && isAfter(day, maxDate)) return true;
    if (allowedDatesSet && !allowedDatesSet.has(toIsoDate(day))) return true;
    return false;
  };

  const selectDay = (day, close) => {
    if (disabled) return;
    if (isDisabledDay(day)) return;
    onChange?.(toIsoDate(day));
    close?.();
  };

  const clear = (close) => {
    if (disabled) return;
    onChange?.("");
    close?.();
  };

  const goToday = (close) => {
    if (disabled) return;
    onChange?.(toIsoDate(today));
    close?.();
  };

  const label = selectedDate ? format(selectedDate, dateFormat) : placeholder;

  const presetItems = Array.isArray(presets) ? presets.filter(Boolean) : [];
  const hasPresets = presetItems.length > 0;

  const isSidebar = buttonPreset === "sidebar";
  const iconAfter = isSidebar && iconPlacement === "end";

  const buttonClass = isSidebar
    ? [
        "inline-flex w-full items-center gap-2 border-0 bg-transparent p-0 text-sm shadow-none outline-none rounded-sm",
        iconAfter ? "justify-end" : "justify-start",
        "cursor-pointer select-none [text-decoration-skip-ink:none] transition-[text-decoration] underline-offset-[3px]",
        "group-hover:underline group-hover:decoration-dotted group-hover:decoration-gray-900/70",
        "focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1",
        "focus-visible:underline focus-visible:decoration-dotted focus-visible:decoration-gray-900/80",
        disabled ? "cursor-not-allowed opacity-60" : "",
        buttonClassName,
      ].join(" ")
    : [
        "flex w-full items-center justify-start gap-2 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm",
        "outline-none",
        disabled ? "cursor-not-allowed opacity-60" : "",
        buttonClassName,
      ].join(" ");

  const labelSpanClass = isSidebar
    ? selectedDate
      ? "text-gray-900 font-semibold"
      : "text-gray-400 italic"
    : selectedDate
      ? "text-gray-900"
      : "text-gray-500";

  const panelAnchor =
    isSidebar && iconAfter
      ? { to: "bottom end", gap: "0.5rem", padding: "0.5rem" }
      : { to: "bottom start", gap: "0.5rem", padding: "0.5rem" };

  const calendarIcon = (
    <CalendarDaysIcon className="h-4 w-4 shrink-0 text-gray-500" aria-hidden />
  );

  return (
    <Popover className={`relative ${className}`}>
      {({ close }) => (
        <>
          <Popover.Button disabled={disabled} className={buttonClass} onPaste={onPaste}>
            {iconAfter ? (
              <>
                <span className={labelSpanClass}>{label}</span>
                {calendarIcon}
              </>
            ) : (
              <>
                {calendarIcon}
                <span className={labelSpanClass}>{label}</span>
              </>
            )}
          </Popover.Button>

          <Transition
            as={Fragment}
            enter="transition ease-out duration-150"
            enterFrom="opacity-0 translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 translate-y-1"
          >
            <Popover.Panel
              portal
              anchor={panelAnchor}
              className={`z-[220] rounded-lg border border-gray-200 bg-white p-3 shadow-lg ${hasPresets ? "flex w-[26rem] gap-3" : "w-80"} ${pickerClassName}`}
            >
              {hasPresets && (
                <div className="w-32 shrink-0 space-y-1 border-r border-gray-200 pr-3">
                  <p className="px-2 pb-1 text-[11px] font-semibold uppercase tracking-widest text-gray-400">
                    Quick ranges
                  </p>
                  {presetItems.map((preset) => (
                    <button
                      key={preset.label}
                      type="button"
                      disabled={disabled}
                      onClick={() => {
                        if (disabled) return;
                        preset.onSelect?.();
                        close?.();
                      }}
                      className={[
                        "block w-full rounded-md px-2 py-1.5 text-left text-sm",
                        preset.isActive ? "bg-gray-900 text-white" : "text-gray-700 hover:bg-gray-100",
                        disabled ? "cursor-not-allowed opacity-60" : "",
                      ].join(" ")}
                    >
                      {preset.label}
                    </button>
                  ))}
                </div>
              )}
              <div className={hasPresets ? "min-w-0 flex-1" : ""}>
              <div className="flex items-center justify-between gap-1 pb-2">
                <button
                  type="button"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => {
                    const next = clampVisibleMonth(subMonths(visibleMonth, 1));
                    setVisibleMonth(next);
                    commitSelectionForVisibleMonth(next);
                  }}
                  disabled={prevMonthDisabled}
                >
                  <ChevronLeftIcon className="h-4 w-4 text-gray-700" />
                </button>
                <div className="flex min-w-0 flex-1 items-center justify-center gap-2">
                  <SelectDropdown
                    options={monthOptions}
                    value={visibleMonth.getMonth() + 1}
                    onChange={(month1to12) => {
                      const next = clampVisibleMonth(
                        new Date(visibleMonth.getFullYear(), Number(month1to12) - 1, 1),
                      );
                      setVisibleMonth(next);
                      commitSelectionForVisibleMonth(next);
                    }}
                    disabled={disabled}
                    selectClassname="!py-1.5 !text-sm min-w-[4.25rem] shrink-0 !bg-white"
                    dropdownClassname="z-[230]"
                    dropdownListClassname="max-h-52"
                  />
                  <SelectDropdown
                    options={yearOptions}
                    value={visibleMonth.getFullYear()}
                    onChange={(y) => {
                      const next = clampVisibleMonth(new Date(Number(y), visibleMonth.getMonth(), 1));
                      setVisibleMonth(next);
                      commitSelectionForVisibleMonth(next);
                    }}
                    disabled={disabled}
                    selectClassname="!py-1.5 !text-sm min-w-[4.25rem] shrink-0 !bg-white"
                    dropdownClassname="z-[230]"
                    dropdownListClassname="max-h-52"
                  />
                </div>
                <button
                  type="button"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                  onClick={() => {
                    const next = clampVisibleMonth(addMonths(visibleMonth, 1));
                    setVisibleMonth(next);
                    commitSelectionForVisibleMonth(next);
                  }}
                  disabled={nextMonthDisabled}
                >
                  <ChevronRightIcon className="h-4 w-4 text-gray-700" />
                </button>
              </div>

              <div className="grid grid-cols-7 gap-1 pb-1 text-center text-xs text-gray-500">
                {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
                  <div key={d} className="py-1">
                    {d}
                  </div>
                ))}
              </div>

              <div className="grid grid-cols-7 gap-1">
                {days.map((day) => {
                  const inMonth = isSameMonth(day, visibleMonth);
                  const isSelected = selectedDate ? isSameDay(day, selectedDate) : false;
                  const isToday = isSameDay(day, today);
                  const dayDisabled = disabled || !inMonth || isDisabledDay(day);

                  return (
                    <button
                      key={day.toISOString()}
                      type="button"
                      disabled={dayDisabled}
                      onClick={() => selectDay(day, close)}
                      className={[
                        "inline-flex h-9 w-9 items-center justify-center rounded-md text-sm",
                        isSelected ? "bg-gray-200 text-gray-900" : "text-gray-900",
                        !isSelected && isToday ? "ring-1 ring-gray-300" : "",
                        !inMonth ? "text-gray-300" : "",
                        dayDisabled ? "cursor-not-allowed opacity-60" : "hover:bg-gray-100",
                      ].join(" ")}
                    >
                      {format(day, "d")}
                    </button>
                  );
                })}
              </div>

              <div className="mt-3 flex items-center justify-between">
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => clear(close)}
                  disabled={disabled}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="text-sm text-blue-600 hover:text-blue-800"
                  onClick={() => goToday(close)}
                  disabled={disabled}
                >
                  Today
                </button>
              </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
};

